import { storage } from '../utils/storage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { MESSAGE_TYPES } from '../constants/background';
import { getPlatform } from '../infrastructure/platform';
import { loggerUtils, createLogContext } from '../utils/loggerUtils';

/**
 * Premium reconciliation core, used by the Stripe/Firestore billing sync
 * (syncPremiumFromFirestore). Deliberately source-agnostic — it was shared
 * with the retired ExtPay path (ADR-010) and stays decoupled so a future
 * backend swap keeps the safety behaviour intact (PREM-002):
 *
 *  - NEVER downgrade on a fetch failure — a flaky connection must not lock
 *    out a paying user. The fetcher signals failure by throwing.
 *  - Apply a 7-day grace period before revoking premium once the backend
 *    first reports "not paid" (guards transient outages / eventual
 *    consistency).
 *  - On any status change, broadcast SETTINGS_UPDATE and
 *    PREMIUM_STATUS_UPDATE so open UI surfaces reload their feature gates.
 */

// How long to keep premium after the billing backend first reports "not paid"
// before actually downgrading.
export const GRACE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export interface StoredSettingsWrapper {
  premiumExpiryAttempts?: number;
  premiumLastChecked?: number;
  premiumUnpaidSince?: number | null;
  settings?: {
    premium_status?: 'free' | 'paid';
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

async function broadcastPremium(wrapper: StoredSettingsWrapper): Promise<void> {
  const isPremium = wrapper.settings?.premium_status === 'paid';
  const bus = getPlatform().bus;
  // "No receiving end" (no UI surface open) is expected and must not fail the
  // reconcile — the swallow used to live in uiHelpers.broadcastMessage.
  await bus.send({ type: MESSAGE_TYPES.SETTINGS_UPDATE, settings: wrapper }).catch(() => {});
  await bus.send({ type: MESSAGE_TYPES.PREMIUM_STATUS_UPDATE, isPremium }).catch(() => {});
}

/**
 * Fetch the paid state from a billing backend and reconcile it into
 * `settings.settings.premium_status`.
 *
 * @param source function name of the caller, for log context (e.g.
 *   'syncPremiumFromFirestore').
 * @param sourceLabel human label for log messages (e.g. 'Stripe billing').
 * @param reason trigger that initiated the sync (startup, alarm, ui-ready…).
 * @param fetchPaid resolves the backend's paid state; MUST throw on
 *   network/timeout/auth failures (a throw means "unknown", never "not
 *   paid").
 * @returns the effective premium state after reconciliation (true = paid).
 */
export async function reconcilePremium(
  source: string,
  sourceLabel: string,
  reason: string,
  fetchPaid: () => Promise<boolean>
): Promise<boolean> {
  const ctx = createLogContext(source, { reason });

  let paid: boolean;
  try {
    paid = await fetchPaid();
  } catch (error) {
    // Network / timeout error: never downgrade on failure — leave whatever is
    // stored untouched so a flaky connection can't lock out a paying user.
    loggerUtils.warning(`Premium sync skipped: ${sourceLabel} status check failed`, {
      ...ctx,
      error: error instanceof Error ? error.message : String(error),
    });
    const stored = (await storage.get(STORAGE_KEYS.SETTINGS)) as StoredSettingsWrapper | null;
    return stored?.settings?.premium_status === 'paid';
  }

  const wrapper = (await storage.get(STORAGE_KEYS.SETTINGS)) as StoredSettingsWrapper | null;
  if (!wrapper || !wrapper.settings) {
    // Settings not initialised yet; nothing to write. ensureSettingsExist runs
    // before the startup sync, so this is only hit in unusual ordering.
    loggerUtils.info('Premium sync: no settings present yet, skipping write', ctx);
    return paid;
  }

  const now = Date.now();
  const currentStatus = wrapper.settings.premium_status;

  if (paid) {
    wrapper.premiumUnpaidSince = null;
    wrapper.premiumExpiryAttempts = 0;
    wrapper.premiumLastChecked = now;

    if (currentStatus !== 'paid') {
      wrapper.settings.premium_status = 'paid';
      await storage.set(STORAGE_KEYS.SETTINGS, wrapper);
      await broadcastPremium(wrapper);
      loggerUtils.info(`Premium unlocked from ${sourceLabel}`, {
        ...ctx,
        previousStatus: currentStatus,
      });
      return true;
    }

    // Already paid — just refresh bookkeeping, no broadcast needed.
    await storage.set(STORAGE_KEYS.SETTINGS, wrapper);
    return true;
  }

  // Backend reports not paid.
  if (currentStatus !== 'paid') {
    // Already free — keep bookkeeping tidy.
    wrapper.premiumLastChecked = now;
    wrapper.premiumUnpaidSince = null;
    await storage.set(STORAGE_KEYS.SETTINGS, wrapper);
    return false;
  }

  // Currently premium but the backend says not paid → apply the grace period
  // before revoking, so a transient blip doesn't yank access from a paying
  // user.
  const unpaidSince = wrapper.premiumUnpaidSince ?? now;
  wrapper.premiumLastChecked = now;
  wrapper.premiumExpiryAttempts = (wrapper.premiumExpiryAttempts ?? 0) + 1;

  if (now - unpaidSince >= GRACE_PERIOD_MS) {
    wrapper.settings.premium_status = 'free';
    wrapper.premiumUnpaidSince = null;
    await storage.set(STORAGE_KEYS.SETTINGS, wrapper);
    await broadcastPremium(wrapper);
    loggerUtils.info('Premium expired after grace period', {
      ...ctx,
      attempts: wrapper.premiumExpiryAttempts,
    });
    return false;
  }

  // Still within grace: keep premium, remember when the unpaid streak began.
  wrapper.premiumUnpaidSince = unpaidSince;
  await storage.set(STORAGE_KEYS.SETTINGS, wrapper);
  loggerUtils.info(`${sourceLabel} reported not paid; holding premium within grace period`, {
    ...ctx,
    unpaidForMs: now - unpaidSince,
    graceMs: GRACE_PERIOD_MS,
  });
  return true;
}
